import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, Drawer, Stack, Select, MenuItem,
  TextField, Button, CircularProgress, Chip, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, Tooltip,
  FormControl,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../api';

// ─── Provider catalogue (same as CareerChatPanel) ────────────────────────────
const PROVIDERS = {
  openai: {
    label: 'OpenAI',
    color: '#10a37f',
    models: [
      { id: 'gpt-4o',      label: 'GPT-4o' },
      { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    ],
  },
  anthropic: {
    label: 'Anthropic',
    color: '#d97706',
    models: [
      { id: 'claude-opus-4-6',          label: 'Claude Opus 4.6' },
      { id: 'claude-sonnet-4-6',         label: 'Claude Sonnet 4.6' },
      { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    ],
  },
  gemini: {
    label: 'Google Gemini',
    color: '#4285f4',
    models: [
      { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
      { id: 'gemini-1.5-pro',   label: 'Gemini 1.5 Pro' },
    ],
  },
  bedrock: {
    label: 'Amazon Bedrock',
    color: '#FF9900',
    models: [
      { id: 'global.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude Sonnet 4.5', group: 'Anthropic' },
      { id: 'us.anthropic.claude-3-5-sonnet-20241022-v2:0',     label: 'Claude 3.5 Sonnet', group: 'Anthropic' },
      { id: 'us.anthropic.claude-3-5-haiku-20241022-v1:0',      label: 'Claude 3.5 Haiku',  group: 'Anthropic' },
      { id: 'us.amazon.nova-pro-v1:0',                          label: 'Nova Pro',           group: 'Amazon' },
      { id: 'us.amazon.nova-lite-v1:0',                         label: 'Nova Lite',          group: 'Amazon' },
    ],
  },
};

const selectSx = {
  color: 'var(--tx-md)',
  fontSize: '0.75rem',
  bgcolor: 'rgba(255,255,255,0.04)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,102,241,0.4)' },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
};

// ─── Markdown-ish text renderer ───────────────────────────────────────────────
function renderBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} style={{ color: 'var(--tx-md)' }}>{part.slice(2, -2)}</strong>
      : part
  );
}

function MessageText({ text }) {
  const lines = text.split('\n');
  return (
    <Box>
      {lines.map((line, i) => {
        if (line.startsWith('## ')) return <Typography key={i} sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--tx-hi)', mt: i > 0 ? 1 : 0 }}>{line.slice(3)}</Typography>;
        if (line.match(/^[-•*] /)) return (
          <Stack key={i} direction="row" gap={0.75} alignItems="flex-start" sx={{ mt: 0.25 }}>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#6366f1', flexShrink: 0, mt: '6px' }} />
            <Typography sx={{ fontSize: '0.78rem', color: 'var(--tx-lo)', lineHeight: 1.55 }}>{renderBold(line.slice(2))}</Typography>
          </Stack>
        );
        if (line === '') return <Box key={i} sx={{ height: 5 }} />;
        return <Typography key={i} sx={{ fontSize: '0.78rem', color: 'var(--tx-lo)', lineHeight: 1.55 }}>{renderBold(line)}</Typography>;
      })}
    </Box>
  );
}

// ─── Key settings dialog ──────────────────────────────────────────────────────
function KeySettingsDialog({ open, onClose, keyStatus, onKeySaved, onKeyDeleted }) {
  const [editing, setEditing] = useState(null);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function startEdit(pid) {
    setEditing(pid);
    setFields(pid === 'bedrock' ? { access_key_id: '', secret_access_key: '', region: 'us-east-1' } : { api_key: '' });
    setErr('');
  }

  async function handleSave(pid) {
    setSaving(true); setErr('');
    try {
      await api.put(`/api/llm-keys/${pid}`, pid === 'bedrock' ? fields : { api_key: fields.api_key });
      onKeySaved(pid);
      setEditing(null);
    } catch { setErr('Failed to save key'); }
    finally { setSaving(false); }
  }

  async function handleDelete(pid) {
    try { await api.delete(`/api/llm-keys/${pid}`); onKeyDeleted(pid); }
    catch { setErr('Failed to remove key'); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#0d1526', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 2 } }}>
      <DialogTitle sx={{ color: 'var(--tx-hi)', fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <SettingsIcon sx={{ color: '#6366f1', fontSize: 18 }} />
          LLM API Keys
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', mb: 2 }}>
          Keys are stored securely in Cloudflare KV and never exposed to the browser.
        </Typography>
        <Stack gap={2}>
          {Object.entries(PROVIDERS).map(([pid, prov]) => {
            const hasKey = keyStatus[pid];
            const isEditing = editing === pid;
            const isBedrock = pid === 'bedrock';
            return (
              <Box key={pid} sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5, p: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={isEditing ? 1.5 : 0}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: prov.color }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--tx-md)' }}>{prov.label}</Typography>
                    {hasKey
                      ? <CheckCircleIcon sx={{ fontSize: 14, color: '#34d399' }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.25)' }} />}
                  </Stack>
                  <Stack direction="row" gap={0.5}>
                    {hasKey && !isEditing && (
                      <Tooltip title="Remove key">
                        <IconButton size="small" onClick={() => handleDelete(pid)} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#fb7185' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!isEditing && (
                      <Button size="small" variant="outlined" onClick={() => startEdit(pid)}
                        sx={{ fontSize: '0.72rem', color: '#818cf8', borderColor: 'rgba(99,102,241,0.4)' }}>
                        {hasKey ? 'Replace' : 'Add Key'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
                {isEditing && !isBedrock && (
                  <Stack direction="row" gap={1} alignItems="center">
                    <TextField autoFocus size="small" fullWidth type="password"
                      placeholder={`Paste ${prov.label} API key…`}
                      value={fields.api_key || ''} onChange={e => setFields({ api_key: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') handleSave(pid); if (e.key === 'Escape') setEditing(null); }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)' } }}
                    />
                    <Button size="small" variant="contained" onClick={() => handleSave(pid)} disabled={saving || !fields.api_key?.trim()}
                      sx={{ bgcolor: '#6366f1', fontSize: '0.72rem', whiteSpace: 'nowrap', minWidth: 60 }}>
                      {saving ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => setEditing(null)} sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>Cancel</Button>
                  </Stack>
                )}
                {isEditing && isBedrock && (
                  <Stack gap={1.25}>
                    <TextField autoFocus size="small" fullWidth label="AWS Access Key ID" placeholder="AKIA..."
                      value={fields.access_key_id || ''} onChange={e => setFields(f => ({ ...f, access_key_id: e.target.value }))}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', color: 'var(--tx-dim)' } }}
                    />
                    <TextField size="small" fullWidth type="password" label="AWS Secret Access Key"
                      value={fields.secret_access_key || ''} onChange={e => setFields(f => ({ ...f, secret_access_key: e.target.value }))}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', color: 'var(--tx-dim)' } }}
                    />
                    <Stack direction="row" gap={1} alignItems="flex-start">
                      <TextField size="small" label="AWS Region" placeholder="us-east-1"
                        value={fields.region || 'us-east-1'} onChange={e => setFields(f => ({ ...f, region: e.target.value }))}
                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', color: 'var(--tx-dim)' } }}
                      />
                      <Stack direction="row" gap={0.75} pt={0.5}>
                        <Button size="small" variant="contained" onClick={() => handleSave(pid)}
                          disabled={saving || !fields.access_key_id?.trim() || !fields.secret_access_key?.trim() || !fields.region?.trim()}
                          sx={{ bgcolor: '#6366f1', fontSize: '0.72rem', whiteSpace: 'nowrap', minWidth: 56 }}>
                          {saving ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : 'Save'}
                        </Button>
                        <Button size="small" onClick={() => setEditing(null)} sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>Cancel</Button>
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Pending update banner ────────────────────────────────────────────────────
function PendingUpdateBanner({ update, onApply, onDiscard }) {
  const fields = Object.keys(update);
  return (
    <Box sx={{
      mx: 2, mb: 1.5,
      bgcolor: 'rgba(99,102,241,0.1)',
      border: '1px solid rgba(99,102,241,0.35)',
      borderRadius: 1.5,
      p: 1.5,
    }}>
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <AutoFixHighIcon sx={{ color: '#818cf8', fontSize: 16, mt: '2px', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#a5b4fc', fontWeight: 600, mb: 0.5 }}>
            Story update ready
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-dim)' }}>
            Changes to: {fields.join(', ')}
          </Typography>
        </Box>
        <Stack direction="row" gap={0.75} flexShrink={0}>
          <Button size="small" onClick={onDiscard}
            sx={{ fontSize: '0.68rem', color: 'var(--tx-dim)', p: '2px 8px', minWidth: 0 }}>
            Discard
          </Button>
          <Button size="small" variant="contained" onClick={onApply}
            sx={{ fontSize: '0.68rem', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, p: '2px 10px', minWidth: 0 }}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

// ─── Suggestions by context ───────────────────────────────────────────────────
const SUGGESTIONS = [
  'Add a quantified result to strengthen the impact',
  'Make the interview form bullets more conversational',
  'Improve the opening sentence of the full story',
  'Add more detail to the action section',
  'Sharpen the situation description to be more compelling',
];

// ─── Main panel ───────────────────────────────────────────────────────────────
export default function InterviewStoryChatPanel({ open, onClose, story, onApplyUpdate }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o');
  const [keyStatus, setKeyStatus] = useState({ openai: false, anthropic: false, gemini: false, bedrock: false });
  const [keyOpen, setKeyOpen] = useState(false);
  const [error, setError] = useState('');
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const bottomRef = useRef(null);

  // Load key status when drawer opens
  useEffect(() => {
    if (!open) return;
    api.get('/api/llm-keys')
      .then(res => setKeyStatus(res.data.providers || {}))
      .catch(() => {});
  }, [open]);

  // Clear messages when a different story is opened
  useEffect(() => {
    if (open) {
      setMessages([]);
      setPendingUpdate(null);
      setError('');
    }
  }, [story?.title, open]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, pendingUpdate]);

  const handleProviderChange = useCallback((pid) => {
    setProvider(pid);
    setModel(PROVIDERS[pid].models[0].id);
  }, []);

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setError('');
    setPendingUpdate(null);

    try {
      const res = await api.post('/api/interview/chat', {
        messages: newMessages,
        story,
        provider,
        model,
      });

      const { reply, story_update } = res.data;
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);

      if (story_update && Object.keys(story_update).length > 0) {
        setPendingUpdate(story_update);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  function handleApply() {
    if (pendingUpdate) {
      onApplyUpdate(pendingUpdate);
      setPendingUpdate(null);
      setMessages(prev => [...prev, {
        role: 'system-notice',
        content: 'Changes applied to the story.',
      }]);
    }
  }

  const providerInfo = PROVIDERS[provider];

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        variant="persistent"
        PaperProps={{
          sx: {
            width: 420,
            bgcolor: 'var(--bg-surf)',
            borderLeft: '1px solid rgba(99,102,241,0.2)',
            display: 'flex',
            flexDirection: 'column',
            top: 52,         // below the AppBar
            height: 'calc(100% - 52px)',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <SmartToyIcon sx={{ color: '#6366f1', fontSize: 19 }} />
              <Typography sx={{ fontWeight: 700, color: 'var(--tx-hi)', fontSize: '0.95rem' }}>AI Story Editor</Typography>
            </Stack>
            <Stack direction="row" gap={0.5}>
              <Tooltip title="Manage API keys">
                <IconButton size="small" onClick={() => setKeyOpen(true)} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#a5b4fc' } }}>
                  <SettingsIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' } }}>
                <CloseIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Stack>
          </Stack>

          {/* Current story context badge */}
          {story && (
            <Box sx={{
              bgcolor: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: 1,
              px: 1.25,
              py: 0.6,
              mb: 1.5,
            }}>
              <Typography sx={{ fontSize: '0.68rem', color: '#818cf8', fontWeight: 600, letterSpacing: 0.3 }}>
                EDITING
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#c7d2fe', lineHeight: 1.3, mt: 0.2 }} noWrap>
                {story.title || 'Untitled story'}
              </Typography>
            </Box>
          )}

          {/* Provider + model */}
          <Stack direction="row" gap={1}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select value={provider} onChange={e => handleProviderChange(e.target.value)} sx={selectSx}
                renderValue={v => (
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: PROVIDERS[v].color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-md)' }}>{PROVIDERS[v].label}</Typography>
                    {keyStatus[v]
                      ? <CheckCircleIcon sx={{ fontSize: 12, color: '#34d399', ml: 'auto' }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', ml: 'auto' }} />}
                  </Stack>
                )}
              >
                {Object.entries(PROVIDERS).map(([pid, prov]) => (
                  <MenuItem key={pid} value={pid} sx={{ fontSize: '0.78rem' }}>
                    <Stack direction="row" alignItems="center" gap={1} width="100%">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: prov.color }} />
                      {prov.label}
                      {keyStatus[pid]
                        ? <CheckCircleIcon sx={{ fontSize: 13, color: '#34d399', ml: 'auto' }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.25)', ml: 'auto' }} />}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select value={model} onChange={e => setModel(e.target.value)} sx={selectSx}>
                {providerInfo.models.map(m => (
                  <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.78rem' }}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {!keyStatus[provider] && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}
              sx={{ bgcolor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 1, px: 1.25, py: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#fbbf24' }}>No API key for {providerInfo.label}</Typography>
              <Button size="small" onClick={() => setKeyOpen(true)}
                sx={{ fontSize: '0.68rem', color: '#fbbf24', p: '2px 8px', minWidth: 0 }}>Add Key</Button>
            </Stack>
          )}
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {messages.length === 0 && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <AutoFixHighIcon sx={{ fontSize: 36, color: 'rgba(99,102,241,0.2)', mb: 1 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', mb: 2.5 }}>
                Ask the AI to improve this story, add details, or refine the interview bullets.
              </Typography>
              <Stack gap={0.75} alignItems="stretch">
                {SUGGESTIONS.map((s, i) => (
                  <Box key={i} onClick={() => setInput(s)}
                    sx={{
                      cursor: 'pointer', textAlign: 'left',
                      bgcolor: 'rgba(99,102,241,0.07)',
                      border: '1px solid rgba(99,102,241,0.18)',
                      borderRadius: 1.5, px: 1.5, py: 0.75,
                      '&:hover': { bgcolor: 'rgba(99,102,241,0.13)', borderColor: 'rgba(99,102,241,0.35)' },
                    }}>
                    <Typography sx={{ fontSize: '0.73rem', color: '#818cf8' }}>{s}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {messages.map((msg, i) => {
            if (msg.role === 'system-notice') {
              return (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#34d399 !important' }} />}
                    label={msg.content}
                    size="small"
                    sx={{ bgcolor: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)', fontSize: '0.7rem' }}
                  />
                </Box>
              );
            }
            return (
              <Box key={i} sx={{ alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: msg.role === 'user' ? '85%' : '100%' }}>
                {msg.role === 'user' ? (
                  <Box sx={{ bgcolor: 'rgba(99,102,241,0.22)', border: '1px solid rgba(99,102,241,0.35)', borderRadius: '12px 12px 2px 12px', px: 1.75, py: 1 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: 'var(--tx-md)', lineHeight: 1.5 }}>{msg.content}</Typography>
                  </Box>
                ) : (
                  <Box sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '2px 12px 12px 12px', px: 1.75, py: 1.25 }}>
                    <Stack direction="row" alignItems="center" gap={0.75} mb={0.75}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: providerInfo.color }} />
                      <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
                        {providerInfo.label} · {model}
                      </Typography>
                    </Stack>
                    <MessageText text={msg.content} />
                  </Box>
                )}
              </Box>
            );
          })}

          {loading && (
            <Box sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '2px 12px 12px 12px', px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={12} sx={{ color: '#6366f1' }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' }}>Thinking…</Typography>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ fontSize: '0.75rem' }} onClose={() => setError('')}>{error}</Alert>}
          <div ref={bottomRef} />
        </Box>

        {/* Pending update banner — sits above the input */}
        {pendingUpdate && (
          <PendingUpdateBanner
            update={pendingUpdate}
            onApply={handleApply}
            onDiscard={() => setPendingUpdate(null)}
          />
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />

        {/* Input */}
        <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
          <Stack direction="row" gap={1} alignItems="flex-end">
            <TextField
              multiline
              maxRows={4}
              fullWidth
              size="small"
              placeholder="Ask the AI to improve this story…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.82rem', color: 'var(--tx-md)', bgcolor: 'rgba(255,255,255,0.04)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
                '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(255,255,255,0.25)', opacity: 1 },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || loading || !keyStatus[provider]}
              sx={{ bgcolor: '#6366f1', color: '#fff', borderRadius: 1.5, width: 38, height: 38, flexShrink: 0, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: 'rgba(99,102,241,0.25)', color: 'rgba(255,255,255,0.3)' } }}
            >
              {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Stack>
          <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.18)', mt: 0.75 }}>
            Enter to send · Shift+Enter for new line
          </Typography>
        </Box>
      </Drawer>

      <KeySettingsDialog
        open={keyOpen}
        onClose={() => setKeyOpen(false)}
        keyStatus={keyStatus}
        onKeySaved={pid => setKeyStatus(prev => ({ ...prev, [pid]: true }))}
        onKeyDeleted={pid => setKeyStatus(prev => ({ ...prev, [pid]: false }))}
      />
    </>
  );
}
